import { useState } from "react";
import { format, isValid, parse } from "date-fns";
import { it } from "date-fns/locale";
import { CalendarDays, ChevronLeft, ChevronRight, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

// Values stay ISO ("yyyy-MM-dd" / "yyyy-MM", local calendar dates, no timezone); only the display is Italian.
const toDate = (v, f) => { const d = v ? parse(String(v), f, new Date()) : undefined; return d && isValid(d) ? d : undefined; };
const MONTHS = ["gen", "feb", "mar", "apr", "mag", "giu", "lug", "ago", "set", "ott", "nov", "dic"];

function Trigger({ label, text, empty, testid, onClear, size = "h-9 min-w-[124px]" }) {
  return (
    <span className="inline-flex items-center">
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" aria-label={label} className={`${size} justify-start gap-1.5 bg-white px-2.5 text-xs font-normal ${text ? "" : "text-slate-400"}`} data-testid={testid}>
          <CalendarDays size={14} className="shrink-0 text-slate-400" />{text || empty}
        </Button>
      </PopoverTrigger>
      {text && onClear && <Button type="button" variant="ghost" size="icon" className={`${size.split(" ")[0]} w-8`} aria-label={`Cancella ${label}`} onClick={onClear} data-testid={`${testid}-clear`}><X size={13} /></Button>}
    </span>);
}

export function DateField({ value, onChange, label, testid, min, max, size, clearable = true }) {
  const [open, setOpen] = useState(false);
  const d = toDate(value, "yyyy-MM-dd");
  const lo = toDate(min, "yyyy-MM-dd");
  const hi = toDate(max, "yyyy-MM-dd");
  const off = [lo && { before: lo }, hi && { after: hi }].filter(Boolean);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Trigger label={label} text={d ? format(d, "dd/MM/yyyy") : ""} empty="gg/mm/aaaa" testid={testid} size={size} onClear={clearable ? () => onChange("") : null} />
      <PopoverContent className="w-auto p-0" align="start" collisionPadding={8} data-testid={`${testid}-popover`}>
        <Calendar mode="single" locale={it} weekStartsOn={1} selected={d} defaultMonth={d || lo || hi} disabled={off.length ? off : undefined}
          onSelect={(x) => { onChange(x ? format(x, "yyyy-MM-dd") : ""); setOpen(false); }} />
      </PopoverContent>
    </Popover>);
}

export function MonthField({ value, onChange, label, testid }) {
  const [open, setOpen] = useState(false);
  const d = toDate(value, "yyyy-MM");
  const [year, setYear] = useState((d || new Date()).getFullYear());
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) setYear((d || new Date()).getFullYear()); }}>
      <Trigger label={label} text={d ? format(d, "MM/yyyy") : ""} empty="mm/aaaa" testid={testid} onClear={() => onChange("")} />
      <PopoverContent className="w-60 p-3" align="start" collisionPadding={8} data-testid={`${testid}-popover`}>
        <div className="mb-2 flex items-center justify-between">
          <Button type="button" variant="ghost" size="icon" className="h-8 w-8" aria-label="Anno precedente" onClick={() => setYear((y) => y - 1)} data-testid={`${testid}-prev-year`}><ChevronLeft size={15} /></Button>
          <span className="text-sm font-medium" data-testid={`${testid}-year`}>{year}</span>
          <Button type="button" variant="ghost" size="icon" className="h-8 w-8" aria-label="Anno successivo" onClick={() => setYear((y) => y + 1)} data-testid={`${testid}-next-year`}><ChevronRight size={15} /></Button>
        </div>
        <div className="grid grid-cols-4 gap-1">
          {MONTHS.map((m, i) => {
            const v = `${year}-${String(i + 1).padStart(2, "0")}`;
            return <Button key={m} type="button" size="sm" variant={v === value ? "default" : "ghost"} className="h-9 text-xs" onClick={() => { onChange(v); setOpen(false); }} data-testid={`${testid}-m-${i + 1}`}>{m}</Button>;
          })}
        </div>
      </PopoverContent>
    </Popover>);
}
